import { Timestamp } from '@angular/fire/firestore';
import { firstValueFrom, Observable } from 'rxjs';

/**
 * Data required to build an appointment details note
 */
export interface AppointmentNoteData {
  scheduledAppointmentType?: string[];
  unscheduledAppointmentType?: string[];
  appointmentCreationTime?: Timestamp;
}

/**
 * Message data used to determine the note timestamp
 */
export interface MessageWithIntent {
  direction?: 'incoming' | 'outgoing';
  intent?: string;
  createdAt: Timestamp;
}

/**
 * Result from building appointment note content
 */
export interface AppointmentNoteResult {
  noteHtml: string;
  createdAt: Timestamp;
}

/**
 * Builds the HTML content for appointment details note
 * @param data - The appointment data containing scheduled and unscheduled appointment types
 * @returns The HTML string for the note or null if no appointment types exist
 */
export function buildAppointmentNoteHtml(data: AppointmentNoteData): string | null {
  const hasScheduled = data.scheduledAppointmentType && data.scheduledAppointmentType.length > 0;
  const hasUnscheduled = data.unscheduledAppointmentType && data.unscheduledAppointmentType.length > 0;

  if (!hasScheduled && !hasUnscheduled) {
    return null;
  }

  let noteHtml = '';

  if (hasScheduled) {
    noteHtml += '<div><span>✓ Impel AI successfully booked appointment for:</span><ul class="mt-1 ml-3 list-disc">';
    data.scheduledAppointmentType!.forEach(appointmentType => {
      noteHtml += `<li>${appointmentType}</li>`;
    });
    noteHtml += '</ul></div>';
  }

  if (hasUnscheduled) {
    noteHtml += '<div class="text-danger"><span>⚠ Please follow up with customer for:</span><ul class="mt-1 ml-3 list-disc">';
    data.unscheduledAppointmentType!.forEach(appointmentType => {
      noteHtml += `<li>${appointmentType}</li>`;
    });
    noteHtml += '</ul></div>';
  }

  return noteHtml;
}

/**
 * Finds the timestamp to use for the appointment note
 * Uses the last incoming message with intent 'scheduleConfirmed' if available, otherwise returns Timestamp.now()
 * @param messages - Array of messages to search through
 * @returns The Timestamp to use for the note's createdAt field
 */
export async function findAppointmentNoteTimestamp(messages$: Observable<MessageWithIntent[]>): Promise<Timestamp> {
  const messages = await firstValueFrom(messages$);
  const scheduleConfirmedMessage = messages
    .filter(msg => msg.direction === 'incoming' && msg.intent === 'scheduleConfirmed')
    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())[0];

  return scheduleConfirmedMessage ? scheduleConfirmedMessage.createdAt : Timestamp.now();
}

/**
 * Builds the complete appointment note result including HTML and timestamp
 * @param data - The appointment data containing scheduled and unscheduled appointment types
 * @param messages - Array of messages to search for the timestamp
 * @returns The AppointmentNoteResult or null if no appointment types exist
 */
export async function buildAppointmentNoteResult(data: AppointmentNoteData, messages$: Observable<MessageWithIntent[]>): Promise<AppointmentNoteResult | null> {
  const noteHtml = buildAppointmentNoteHtml(data);

  if (!noteHtml) {
    return null;
  }

  return {
    noteHtml,
    createdAt: data.appointmentCreationTime ?? (await findAppointmentNoteTimestamp(messages$)),
  };
}

/**
 * The system user object used for appointment notes
 */
export const APPOINTMENT_NOTE_SYSTEM_USER = {
  agentAvatarUrl: '',
  agentId: 'system',
  agentName: 'Impel',
};
